import Phaser from 'phaser';
import { COLORS } from '../style.js';

/**
 * Mobile-style sliding toggle. Rounded pill track with a circular
 * thumb that animates between left (off) and right (on) positions.
 * Track fill flips between muted gray (off) and cyan (on) to read
 * at a glance.
 *
 * Wrapped in a Container so callers can `setDepth`, add to a scroll
 * container, etc. with one reference. The hit area is a transparent
 * interactive Rectangle inside the container — tapping anywhere on
 * the pill fires onChange.
 *
 * Latency handling: the consumer controls when the value actually
 * commits. onChange fires immediately with the requested new value;
 * if the consumer needs to await an API call, they should:
 *
 *   1. setEnabled(false)
 *   2. await the API
 *   3. on success: setEnabled(true) (the optimistic flip stays)
 *   4. on failure: setValue(!newValue, false) + setEnabled(true)
 *
 * The widget itself never blocks taps — disabled taps are silently
 * swallowed by the input check in the handler.
 */

const TRACK_WIDTH = 88;
const TRACK_HEIGHT = 44;
const TRACK_RADIUS = TRACK_HEIGHT / 2;
const THUMB_RADIUS = (TRACK_HEIGHT - 8) / 2;
const ANIM_MS = 160;

const TRACK_OFF_COLOR = COLORS.borderBrightInt;
const TRACK_ON_COLOR = COLORS.accentCyanInt;
const THUMB_COLOR = 0xe8eef5;

export interface SwitchOptions {
  readonly scene: Phaser.Scene;
  readonly x: number;
  readonly y: number;
  readonly initialValue: boolean;
  readonly onChange: (value: boolean) => void;
}

export class Switch {
  readonly root: Phaser.GameObjects.Container;
  private readonly scene: Phaser.Scene;
  private readonly onChange: (value: boolean) => void;
  private readonly track: Phaser.GameObjects.Graphics;
  private readonly thumb: Phaser.GameObjects.Arc;
  private readonly hit: Phaser.GameObjects.Rectangle;

  private value: boolean;
  private enabled = true;

  constructor(opts: SwitchOptions) {
    this.scene = opts.scene;
    this.onChange = opts.onChange;
    this.value = opts.initialValue;

    this.root = opts.scene.add.container(opts.x, opts.y);

    this.track = opts.scene.add.graphics();
    this.drawTrack();
    this.root.add(this.track);

    this.thumb = opts.scene.add.arc(
      this.thumbXForValue(this.value),
      0,
      THUMB_RADIUS,
      0,
      360,
      false,
      THUMB_COLOR
    );
    this.root.add(this.thumb);

    // Transparent hit area covers the whole pill. Sits at z-top of
    // the container so taps don't get eaten by sub-shapes.
    this.hit = opts.scene.add
      .rectangle(0, 0, TRACK_WIDTH, TRACK_HEIGHT, 0x000000, 0)
      .setInteractive({ useHandCursor: true });
    this.hit.on('pointerup', () => this.handleTap());
    this.root.add(this.hit);
  }

  /**
   * Current value. Updated immediately when the user taps (the
   * onChange callback fires after).
   */
  getValue(): boolean {
    return this.value;
  }

  /**
   * Programmatic update. Used by the consumer to revert an optimistic
   * change after a failed API call, or to seed initial state from a
   * server response. Does NOT fire onChange.
   */
  setValue(next: boolean, animate = true): void {
    if (next === this.value) return;
    this.value = next;
    this.applyValue(animate);
  }

  /**
   * Disable taps. Track + thumb fade to half opacity so the disabled
   * state reads visually. Re-enable to restore.
   */
  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    this.root.setAlpha(enabled ? 1 : 0.45);
  }

  setDepth(depth: number): this {
    this.root.setDepth(depth);
    return this;
  }

  destroy(): void {
    this.root.destroy();
  }

  // --- Internals ---

  private handleTap(): void {
    if (!this.enabled) return;
    const next = !this.value;
    this.value = next;
    this.applyValue(true);
    this.onChange(next);
  }

  private applyValue(animate: boolean): void {
    this.drawTrack();
    const targetX = this.thumbXForValue(this.value);
    if (!animate) {
      this.thumb.x = targetX;
      return;
    }
    this.scene.tweens.add({
      targets: this.thumb,
      x: targetX,
      duration: ANIM_MS,
      ease: 'Cubic.easeOut',
    });
  }

  private drawTrack(): void {
    this.track.clear();
    const fill = this.value ? TRACK_ON_COLOR : TRACK_OFF_COLOR;
    this.track.fillStyle(fill, 1);
    this.track.fillRoundedRect(
      -TRACK_WIDTH / 2,
      -TRACK_HEIGHT / 2,
      TRACK_WIDTH,
      TRACK_HEIGHT,
      TRACK_RADIUS
    );
  }

  private thumbXForValue(value: boolean): number {
    const inset = THUMB_RADIUS + 4;
    return value ? TRACK_WIDTH / 2 - inset : -TRACK_WIDTH / 2 + inset;
  }
}
